import React from 'react';
import { createComponentPreviewEntry } from '@wix/editor-elements-integrations';
import type {
  FreemiumBannerOdeditorMapperProps,
  FreemiumBannerOdeditorProps,
} from '../FreemiumBannerOdeditor.types';
import style from './style/FreemiumBannerOdeditorPreview.scss';
import classNamesFn from 'classnames';
import Link from '../../../Link/viewer/Link';
import { builderUpgradeClick } from '@wix/bi-logger-site-builders-data/v2';
import webBiLogger from '@wix/web-bi-logger';

export const FreemiumBannerOdeditorPreview: React.FC<
  FreemiumBannerOdeditorProps
> = ({
  id,
  textBeforeLogo = 'Upgrade your website to remove Wix ads',
  textAfterLogo = '',
  buttonText = 'Upgrade Now',
  direction,
  className,
  href,
  children,
  logger,
}) => {
  const reportUpgradeClick = () => {
    if (!logger) {
      return;
    }
    try {
      logger.report(
        builderUpgradeClick({
          working_mode: 'preview',
          name: 'upgrade_now',
          action_context: 'preview_free_site_banner',
          origin: 'topbar',
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to report upgrade click');
    }
  };

  return (
    <div
      id={id}
      className={classNamesFn(className, style.root)}
      dir={direction}
    >
      <Link
        className={style.link}
        href={href}
        target="_blank"
        rel="nofollow"
        onClick={reportUpgradeClick}
      >
        <span className={style.contents}>
          <span data-hook="freemium-text" className={style.text}>
            {textBeforeLogo}
            {children}
            {textAfterLogo}
          </span>
          <button data-hook="freemium-button" className={style.button}>
            {buttonText}
          </button>
        </span>
      </Link>
    </div>
  );
};

export const withComponentPreview = (
  _ViewerComponent: React.ComponentType<FreemiumBannerOdeditorProps>,
) => {
  return (props: FreemiumBannerOdeditorProps) => {
    const logger = React.useMemo(() => {
      try {
        return webBiLogger.factory().logger();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to initialize BI logger');
        return null;
      }
    }, []);

    return (
      <FreemiumBannerOdeditorPreview {...props} logger={logger ?? undefined} />
    );
  };
};

export default (
  ViewerComponent: React.ComponentType<FreemiumBannerOdeditorProps>,
) =>
  createComponentPreviewEntry<
    FreemiumBannerOdeditorProps,
    FreemiumBannerOdeditorMapperProps
  >(withComponentPreview(ViewerComponent));
